import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../lib/api';
import { PageLoading } from '../../components/LoadingSpinner';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage, isAlreadyMember } from '../../lib/apiErrors';

/**
 * Landing route for invite links: /groups/join/:code
 * - Logged in  -> joins the group immediately, then goes to /groups
 * - Logged out -> stores the code and sends the visitor to register;
 *   Layout picks up the pending code right after they sign in.
 */
export function JoinGroupPage() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const attempted = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (loading || attempted.current) return;
    const cleanCode = (code || '').trim().toUpperCase();
    if (!cleanCode) { navigate('/'); return; }

    if (!user) {
      try { localStorage.setItem('pending_group_code', cleanCode); } catch {}
      toast.info(t('groups.loginToJoin', 'Create an account or log in to join the group'));
      navigate('/register');
      return;
    }

    attempted.current = true;
    userAPI.joinGroup(cleanCode)
      .then(() => toast.success(t('groups.joinedToast', 'Joined the group! 🎉')))
      .catch((err) => {
        if (isAlreadyMember(err)) {
          toast.info(t('groups.alreadyMember', 'You are already in this group'));
        } else {
          toast.error(apiErrorMessage(err, t, 'groups.joinCheckCode'));
        }
      })
      .finally(() => navigate('/groups'));
  }, [loading, user, code, navigate, t]);

  return <PageLoading />;
}
